import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'src/analytics/KatalLogger';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';
import {
  getCostCentersForBusinessGroup,
  getProductLinesForBusinessGroup,
  getTransformedCorpSegments
} from 'src/features/business-group/forecast-template/forecast-utils/CorpSegmentsUtils';
import { BusinessGroupEntity } from 'src/models/AppContextModels';
import { CostCenterAndProductLineValuesPerBusinessGroup, OptionDropdown } from 'src/models/ForecastModels';
import { RootState } from '../store';

export const fetchMasterCorpSegmentDropdownValues = createAsyncThunk(
  'forecastTemplate/fetchMasterCorpSegmentDropdownValues',
  async ({ businessGroup }: { businessGroup: BusinessGroupEntity }, { getState }) => {
    const state = getState() as RootState;
    const masterCorpSegments = state.corpSegmentsStore.masterCorpSegments;

    if (!masterCorpSegments) {
      logger.warn('Master corp segments data is not available');
      return [];
    }

    const corpSegments = await getTransformedCorpSegments(businessGroup.corp_segments, masterCorpSegments);
    return corpSegments;
  }
);

/**
 * Used in AppInitializer with all active Business Groups. Available at root level of the App.
 * Redux thunk to fetch cost center and product line dropdown values for all active business groups
 * @returns Array of CostCenter and Product Line Values Per BusinessGroup containing cost centers and product lines per business group
 */
export const fetchCCAndPLDropdownValuesOfAllActiveBusinessGroups = createAsyncThunk(
  'forecastTemplate/fetchCCAndPLDropdownValuesOfAllActiveBusinessGroups',
  async (_, { getState }) => {
    logger.debug('fetching CC and PL Dropdown Values Of Active Business Groups started');
    const startTime = Date.now();

    const rootState = getState() as RootState;

    // Get required data from state
    const activeBusinessGroups = rootState.businessGroupStore.businessGroups.filter((group) => group.item_metadata.is_active);
    const masterCorpSegments = rootState.corpSegmentsStore.masterCorpSegments;

    logger.debug(`Processing ${activeBusinessGroups.length} active business groups`);

    // Find master segment info for cost centers and product lines
    const masterCostCenterInfo = masterCorpSegments?.find((segment) => segment.segment_name === CorpSegmentNames.COST_CENTER);
    const masterProductLineInfo = masterCorpSegments?.find((segment) => segment.segment_name === CorpSegmentNames.PRODUCT_LINE);

    // Validate required data exists
    if (!activeBusinessGroups || !masterCostCenterInfo || !masterProductLineInfo) {
      return [];
    }

    // Process each active business group in parallel
    const businessGroupPromises = activeBusinessGroups.map(async (businessGroup) => {
      const bgStartTime = Date.now();

      try {
        // Fetch cost centers and product lines in parallel within each BG
        const [costCentersDropdownValues, productLinesDropdownValues] = await Promise.all([
          getCostCentersForBusinessGroup(businessGroup.corp_segments, masterCostCenterInfo),
          getProductLinesForBusinessGroup(businessGroup.corp_segments, masterProductLineInfo)
        ]);

        const costCenters = costCentersDropdownValues.masterCorpSegmentDropdownValues.map((option: OptionDropdown) => option.label);
        const productLines = productLinesDropdownValues.masterCorpSegmentDropdownValues.map((option: OptionDropdown) => option.label);

        return {
          dataClassificationId: businessGroup.data_classification.data_classification_id!,
          data_classification_short_description: businessGroup.data_classification.data_classification_short_description,
          costCenters: costCenters?.sort((a, b) => a.localeCompare(b)),
          productLines: productLines?.sort((a, b) => a.localeCompare(b))
        };
      } catch (error: any) {
        logger.error('Error fetching cost center data for business group:', {
          ...error,
          businessGroup: businessGroup.data_classification.data_classification_name
        });
        return null;
      }
    });

    const results = await Promise.all(businessGroupPromises);
    const eligibleCCAndPLValuesOfAllBGs = results.filter((result) => result !== null) as CostCenterAndProductLineValuesPerBusinessGroup[];

    logger.debug(`fetching CC and PL Dropdown Values Of Active Business Groups completed in ${Date.now() - startTime}ms`);
    return eligibleCCAndPLValuesOfAllBGs;
  }
);
